import React, { useEffect, useState } from "react";
// Assuming the service path is correct based on the previous conversation
import InvestorEnquiryManager from "../../services/home/InvestorEnquiryManager";

// NOTE: External libraries like 'xlsx', 'file-saver', and 'react-icons' are not available
// in this environment. We are replacing the export logic with pure JS CSV generation
// and using inline SVGs/Emojis for icons to ensure the component is runnable.

/**
 * Manages the display and export of all Investor Enquiries.
 * This component fetches data using the live API service.
 */
const InvestorEnquiryManagerPage = () => {
    // State to hold the fetched enquiry data
    const [enquiries, setEnquiries] = useState([]);
    // State to manage loading status
    const [loading, setLoading] = useState(true);
    // State for showing success/error messages 
    const [actionMessage, setActionMessage] = useState("");

    useEffect(() => {
        fetchEnquiries();
    }, []);

    /**
     * Fetches all investor enquiries from the backend.
     */
    const fetchEnquiries = async () => {
        try {
            setLoading(true);
            const response = await InvestorEnquiryManager.getAllEnquiries();
            // Assuming the list of enquiries is nested under 'responseData'
            const data = response.responseData || response; 

            // Sort by creation date descending
            const sortedData = (Array.isArray(data) ? data : []).sort(
                (a, b) => new Date(b.creationDate) - new Date(a.creationDate)
            );
            setEnquiries(sortedData);
            setActionMessage("");
        } catch (error) {
            console.error("Failed to fetch enquiries:", error);
            setActionMessage("Failed to load enquiries. Check console for details.");
            setEnquiries([]);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Helper to format array fields (like interests or ranges) for concise table display.
     */
    const formatListField = (list) => {
        if (!list || list.length === 0) return 'N/A';
        const firstItem = list[0];
        const remainingCount = list.length - 1;
        
        return remainingCount > 0 ? `${firstItem} (+${remainingCount})` : firstItem;
    };

    /**
     * Maps the enquiry data into a flat, readable structure for export.
     */
    const mapEnquiriesForExport = () => {
        return enquiries.map((e) => ({
            ID: e.enquiryId || "N/A",
            "Full Name": e.fullName || "",
            Email: e.email || "",
            Phone: e.phone || "",
            Company: e.company || "N/A",
            Designation: e.designation || "N/A",
            "Engagement Type": e.engagementType || "N/A",
            Interests: (e.interests || []).join(' | '),
            "Other Interest": e.otherInterest || "",
            "Preferred Range": (e.preferredInvestmentRange || []).join(' | '),
            Message: e.message || "",
            "Created At": e.creationDate 
                ? new Date(e.creationDate).toLocaleString()
                : "",
        }));
    };

    /**
     * Handles the download of data in CSV format using pure JavaScript.
     */
    const handleDownloadCSV = () => {
        const exportData = mapEnquiriesForExport();
        if (exportData.length === 0) {
             setActionMessage("Cannot export, no data found.");
             return;
        }

        const headers = Object.keys(exportData[0]);
        const csv = [
            headers.join(','), // Header row
            ...exportData.map(row => headers.map(fieldName => {
                // Escape special characters and wrap in quotes
                const value = String(row[fieldName]).replace(/"/g, '""');
                return `"${value}"`;
            }).join(','))
        ].join('\n');

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", "investor_enquiries.csv");
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        setActionMessage("CSV export initiated successfully.");
    };

    /**
     * Handles the download of data in a simulated Excel format (just CSV with an XLSX file extension 
     * since the XLSX library is unavailable).
     */
    const handleDownloadExcel = () => {
        // Since XLSX library is not available, we fall back to a CSV but save it as .xlsx
        // to match the expected button functionality, informing the user of the limitation.
        const exportData = mapEnquiriesForExport();
        if (exportData.length === 0) {
             setActionMessage("Cannot export, no data found.");
             return;
        }

        const headers = Object.keys(exportData[0]);
        const csv = [
            headers.join(','),
            ...exportData.map(row => headers.map(fieldName => {
                const value = String(row[fieldName]).replace(/"/g, '""');
                return `"${value}"`;
            }).join(','))
        ].join('\n');

        // Create blob and link to download
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", "investor_enquiries_simulated.csv"); // Change extension for clarity
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        setActionMessage("Excel (Simulated CSV) export initiated successfully.");
    };


    // Simple SVG for "Download CSV" icon (FaFileCsv replacement)
    const CsvIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 2a8 8 0 100 16A8 8 0 0010 2zm1 11a1 1 0 10-2 0v-4a1 1 0 102 0v4zm-1-7a1 1 0 110 2 1 1 0 010-2z" clipRule="evenodd" />
        </svg>
    );

    // Simple SVG for "Download Excel" icon (FaFileExcel replacement)
    const ExcelIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM4 11a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM4 15a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1z" />
        </svg>
    );

    // Simple SVG for "Total Users" icon (FaUsers replacement)
    const UsersIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.972 5.972 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
        </svg>
    );

    // Simple SVG for "Spinner" icon (FaSpinner replacement)
    const SpinnerIcon = () => (
        // Replaced text-indigo-500 with text-primary-DEFAULT
        <svg className="animate-spin text-primary mr-3 h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
    );

    // Simple SVG for Rupee Sign (replacing DollarSign)
    const RupeeSignIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3 text-primary dark:text-primary-shade2">
            <path d="M6 3h12" />
            <path d="M6 8h12" />
            <path d="M6 13l8.5 8" />
            <path d="M6 13h3" />
            <path d="M9 13c6.667 0 6.667-10 0-10" />
        </svg>
    );

    // Simple SVG for Briefcase (lucide-react replacement)
    const BriefcaseIcon = () => (
        // Kept gray-400 for a muted/placeholder look
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-gray-400 mb-4">
            <rect width="20" height="14" x="2" y="7" rx="2" ry="2" />
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
    );

    // Helper to get initials for avatar
    function getInitials(name) {
        if (!name) return "IN";
        const parts = name.trim().split(/\s+/);
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }

    return (
        <div className="p-6 font-nunito bg-gray-50 dark:bg-gray-900 min-h-screen space-y-5">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-600 dark:text-orange-500">
                        <path d="M6 3h12" />
                        <path d="M6 8h12" />
                        <path d="M6 13l8.5 8" />
                        <path d="M6 13h3" />
                        <path d="M9 13c6.667 0 6.667-10 0-10" />
                    </svg>
                    Investor Enquiries
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Real-time view of all incoming investment interest.</p>
            </div>

            {/* Stats + Action Bar */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-3 border border-gray-200/80 dark:border-gray-700/80 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400">
                        Total Enquiries
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {enquiries.length}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={handleDownloadCSV}
                        disabled={enquiries.length === 0}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium shadow-sm hover:shadow transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <CsvIcon />
                        Download CSV
                    </button>
                    <button
                        onClick={handleDownloadExcel}
                        disabled={enquiries.length === 0}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium shadow-sm hover:shadow transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ExcelIcon />
                        Download Excel
                    </button>
                </div>
            </div>
            
            {actionMessage && (
                <div className={`p-3 rounded-lg text-sm font-medium ${actionMessage.includes("Failed") 
                    ? "bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300 border border-red-200/80 dark:border-red-700/50" 
                    : "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 border border-blue-200/80 dark:border-blue-700/50"}`}> 
                    {actionMessage}
                </div>
            )}

            {/* Table or Loading State */}
            {loading ? (
                <div className="flex flex-col justify-center items-center py-20 bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 shadow-sm">
                    <SpinnerIcon />
                    <p className="text-gray-600 dark:text-gray-300 text-sm">Fetching investor enquiries...</p>
                </div>
            ) : enquiries.length === 0 ? (
                <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 shadow-sm">
                    <BriefcaseIcon />
                    <p className="text-gray-600 dark:text-gray-300 text-sm">No investor enquiries found yet.</p>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm min-w-[900px]">
                            <thead>
                                <tr className="border-b border-gray-200/80 dark:border-gray-700/80 bg-gray-50/50 dark:bg-gray-900/50">
                                    <th className="px-4 py-3">
                                        <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400">
                                            Investor Name
                                        </span>
                                    </th>
                                    <th className="px-4 py-3">
                                        <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400">
                                            Contact Info
                                        </span>
                                    </th>
                                    <th className="px-4 py-3">
                                        <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400">
                                            Company / Role
                                        </span>
                                    </th>
                                    <th className="px-4 py-3">
                                        <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400">
                                            Engagement
                                        </span>
                                    </th>
                                    <th className="px-4 py-3">
                                        <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400">
                                            Investment Range
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                                {enquiries.map((enquiry, index) => {
                                    const investmentRanges = enquiry.preferredInvestmentRange || [];
                                    const firstRange = investmentRanges[0] || 'N/A';
                                    const remainingCount = investmentRanges.length - 1;

                                    return (
                                        <tr
                                            key={enquiry.enquiryId || index}
                                            className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-all duration-150"
                                        >
                                            <td className="px-4 py-3.5">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 dark:from-orange-900/50 dark:to-orange-800/50 text-orange-700 dark:text-orange-300 flex items-center justify-center font-semibold shadow-sm">
                                                        <span className="text-xs">{getInitials(enquiry.fullName)}</span>
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-semibold text-gray-900 dark:text-white">{enquiry.fullName || 'N/A'}</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">{enquiry.email || 'N/A'}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3.5">
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{enquiry.phone || 'N/A'}</span>
                                            </td>
                                            <td className="px-4 py-3.5">
                                                <div>
                                                    <div className="text-sm font-semibold text-gray-900 dark:text-white">{enquiry.company || 'N/A'}</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">{enquiry.designation || 'N/A'}</div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3.5">
                                                <span className="inline-flex px-2.5 py-1 text-[11px] font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200/80 dark:border-blue-700/50 shadow-sm">
                                                    {enquiry.engagementType || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3.5">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                                                        ₹ {firstRange}
                                                    </span>
                                                    {remainingCount > 0 && (
                                                        <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                                            +{remainingCount}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvestorEnquiryManagerPage;